/**
 * The right-hand side of one upload mapping row: what Wend detected the column
 * as, and — where it means something — how much of the sample matched.
 *
 * ## WHY THIS IS ITS OWN FILE
 *
 * It was inline in `ColumnMappingForm`, which reaches the API client and so
 * cannot be rendered by a verify script. The two rules below are both about
 * what a number on this screen is allowed to claim, and a rule about honesty
 * that only a source-text probe holds is held weakly. Here it is a component
 * `verify-list-building` renders with a Text column and a Phone column side by
 * side.
 *
 * ## THE FIGURE HAS A VISIBLE WORD
 *
 * The first slice 66 build drew "PHONE ▬ 62%" with the meaning only in
 * `sr-only` text. A sighted uploader saw a bare percentage in a status colour
 * and nothing saying what it was a share of. The word "matched" now sits
 * before the meter (`aria-hidden`, because the screen-reader sentence already
 * says "matched type in 62% of sampled non-empty cells" and would otherwise
 * hear it twice).
 *
 * ## TEXT HAS NO MATCH SHARE
 *
 * For a column detected as Text, `confidence` is the share of cells that are
 * plain text — and Text is the detector's FALLBACK, the type a column gets
 * when no other shape reached its threshold. That number is not a match
 * quality. Drawn as one, a "Notes" column holding "12, call later, 33" showed
 * an amber 40% and flagged a problem with a column that has none, on the
 * screen whose own copy says "check them rather than trusting them" (slice 66
 * review). So a Text column shows its type tag and no meter, no percentage
 * and no tone. The rejected alternative, `percent={null}`, would draw the
 * not-measured dash and tell a screen reader the share was unknown, when the
 * truth is that it does not exist.
 *
 * ## ZERO IS NOT "NOT MEASURED"
 *
 * A column with no values in the sample has no share at all — the API stores
 * `0` for it — so it passes `null` and draws the no-figure dash, never "0%",
 * which would claim every value failed.
 */
import type { DetectedColumn } from '@wend/shared';
import { CoverageMeter, Tag } from '@wend/ui';

import { COLUMN_TYPE_LABEL } from '../../lib/dataset-display';

export function MatchShare({ column }: { column: DetectedColumn }) {
  const sampled = column.sampleValues.length > 0;

  return (
    <span className="flex items-center gap-2">
      <span className="sr-only">Detected as </span>
      <Tag tone="neutral">{COLUMN_TYPE_LABEL[column.type]}</Tag>
      {column.type !== 'text' && (
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="text-[0.72rem] text-muted">
            matched
          </span>
          <CoverageMeter basis="matched" compact percent={sampled ? column.confidence * 100 : null} />
        </span>
      )}
    </span>
  );
}
